import React, { useState, useEffect } from 'react';
import { buildApiUrl } from '../config/api';

interface ServiceTeamSummary {
  teamId: string;
  teamName: string;
  description: string;
  totalMembers: number;
  activeMembers: number;
  recentActivity: string | null;
  averageServicesPerMember: number;
  teamType: string;
}

interface ServiceFrequencyData {
  memberId: string;
  memberName: string;
  totalTeams: number;
  totalServices: number;
  monthsActive: number;
  currentTeams: string[];
  lastServiceDate: string | null;
  serviceFrequency: 'Very High' | 'High' | 'Medium' | 'Low' | 'Inactive';
  journeyStage: string;
}

interface ServiceAnalytics {
  totalServiceTeams: number;
  totalServingMembers: number;
  averageServicesPerMember: number;
  mostActiveTeams: ServiceTeamSummary[];
  serviceFrequencyDistribution: {
    veryHigh: number;
    high: number;
    medium: number;
    low: number;
    inactive: number;
  };
  memberServiceData: ServiceFrequencyData[];
}

const ServiceAnalytics: React.FC = () => {
  const [serviceData, setServiceData] = useState<ServiceAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchServiceAnalytics();
  }, []);

  const fetchServiceAnalytics = async () => {
    try {
      setLoading(true);
      const response = await fetch(buildApiUrl('/service-analytics?limit=100'));
      const result = await response.json();
      
      if (result.success) {
        setServiceData(result.data);
      } else {
        setError(result.error?.message || 'Failed to fetch service analytics');
      }
    } catch (err) {
      setError('Failed to fetch service analytics');
      console.error('Error fetching service analytics:', err);
    } finally {
      setLoading(false);
    }
  };

  const getFrequencyColor = (frequency: string) => {
    switch (frequency) {
      case 'Very High':
        return 'bg-green-500';
      case 'High':
        return 'bg-blue-500';
      case 'Medium':
        return 'bg-yellow-500';
      case 'Low':
        return 'bg-orange-500';
      case 'Inactive':
        return 'bg-gray-400';
      default:
        return 'bg-gray-400';
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4">
        <div className="flex">
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">Error Loading Service Analytics</h3>
            <div className="mt-2 text-sm text-red-700">
              <p>{error}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!serviceData) {
    return <div>No service data available</div>;
  }

  const frequencyData = [
    { label: 'Very High', count: serviceData.serviceFrequencyDistribution.veryHigh, color: 'bg-green-500' },
    { label: 'High', count: serviceData.serviceFrequencyDistribution.high, color: 'bg-blue-500' },
    { label: 'Medium', count: serviceData.serviceFrequencyDistribution.medium, color: 'bg-yellow-500' },
    { label: 'Low', count: serviceData.serviceFrequencyDistribution.low, color: 'bg-orange-500' },
    { label: 'Inactive', count: serviceData.serviceFrequencyDistribution.inactive, color: 'bg-gray-400' }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h1 className="text-2xl font-bold text-gray-900">Service Analytics</h1>
          <p className="text-gray-600 mt-1">Track team memberships and service frequency across your church</p>
        </div>
      </div>

      {/* Overview Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                  <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Service Teams</dt>
                  <dd className="text-lg font-medium text-gray-900">{serviceData.totalServiceTeams}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                  <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                  </svg>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Serving Members</dt>
                  <dd className="text-lg font-medium text-gray-900">{serviceData.totalServingMembers}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center">
                  <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Avg Services/Member</dt>
                  <dd className="text-lg font-medium text-gray-900">{serviceData.averageServicesPerMember}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Service Frequency Distribution */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Service Frequency Distribution</h2>
          <p className="text-sm text-gray-600 mt-1">How often members are serving across all teams</p>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-5 gap-4">
            {frequencyData.map((item) => (
              <div key={item.label} className="text-center">
                <div className={`w-full h-24 ${item.color} rounded-lg flex items-end justify-center text-white font-bold text-lg mb-2`}>
                  {item.count}
                </div>
                <div className="text-sm font-medium text-gray-900">{item.label}</div>
                <div className="text-xs text-gray-500">members</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Most Active Teams */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Most Active Service Teams</h2>
          <p className="text-sm text-gray-600 mt-1">Teams with the highest member engagement</p>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Team</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Members</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Active</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Avg Services</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Activity</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {serviceData.mostActiveTeams.map((team) => (
                <tr key={team.teamId} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div>
                      <div className="text-sm font-medium text-gray-900">{team.teamName}</div>
                      <div className="text-sm text-gray-500">{team.description}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {team.totalMembers}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                      {team.activeMembers}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {team.averageServicesPerMember}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(team.recentActivity)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Top Serving Members */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Most Active Serving Members</h2>
          <p className="text-sm text-gray-600 mt-1">Members with the highest service frequency</p>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Member</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Teams</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Services</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Frequency</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Journey Stage</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Service</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {serviceData.memberServiceData.slice(0, 20).map((member) => (
                <tr key={member.memberId} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="text-sm font-medium text-gray-900">{member.memberName}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{member.totalTeams}</div>
                    <div className="text-xs text-gray-500">{member.monthsActive} months active</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{member.totalServices}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full text-white ${getFrequencyColor(member.serviceFrequency)}`}>
                      {member.serviceFrequency}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                      {member.journeyStage}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(member.lastServiceDate)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ServiceAnalytics;
